import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const MyAnonymousIdentities = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [identities, setIdentities] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser) {
      fetchMyAnonymousIdentities();
    }
  }, [currentUser]);

  const fetchMyAnonymousIdentities = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError('');

      // Get user's identities directly from the userAnonymousIdentities collection
      const q = query(
        collection(db, 'userAnonymousIdentities'),
        where('userId', '==', currentUser.uid)
      );
      const identitiesSnapshot = await getDocs(q);

      const allIdentities = [];

      for (const identityDoc of identitiesSnapshot.docs) {
        const identity = identityDoc.data();

        // Get tournament info
        const tournamentDoc = await getDoc(doc(db, 'tournaments', identity.tournamentId));

        // Get anonymous name
        const nameDoc = await getDoc(identity.nameRef);

        if (tournamentDoc.exists() && nameDoc.exists()) {
          allIdentities.push({
            id: identityDoc.id,
            tournamentId: identity.tournamentId,
            tournamentName: tournamentDoc.data().name,
            anonymousName: nameDoc.data().name,
            assignedAt: identity.assignedAt?.toDate?.() || new Date(),
          });
        }
      }

      setIdentities(allIdentities);
    } catch (err) {
      console.error('Error fetching anonymous identities:', err);
      setError('Failed to load your anonymous identities');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mt: 3,
        bgcolor: '#123',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.12)',
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: '#FFD700' }}>
        Your Anonymous Tournament Identities
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
        Only you can see these identities. Keep your identity secret during tournaments!
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} sx={{ color: 'white' }} />
        </Box>
      ) : identities.length > 0 ? (
        <List sx={{ bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1 }}>
          {identities.map((identity, index) => (
            <React.Fragment key={identity.id}>
              {index > 0 && <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />}
              <ListItem>
                <ListItemText
                  primary={
                    <Typography sx={{ color: 'white' }}>
                      In <strong>{identity.tournamentName}</strong>, you are playing as:
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#FFD700',
                        fontWeight: 'bold',
                        mt: 1,
                      }}
                    >
                      {identity.anonymousName}
                    </Typography>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Alert
          severity="info"
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            color: 'white',
            '.MuiAlert-icon': { color: 'white' },
          }}
        >
          You are not currently participating in any anonymous tournaments.
        </Alert>
      )}
    </Paper>
  );
};

export default MyAnonymousIdentities;
